import { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import {
  setFetcherSettingsDetailsData,
  setIsDetailsLoading,
} from "../../../../features/fetcherSettingsSlice";
import { FetcherSettingsEndPointLogic } from "../../../../logic/DataCollection/FetcherSettings/FetcherSettingsEndPointLogic";
import {
  rowId,
  selectCatalogueRows,
  selectDetailBrands,
} from "../helpers/fetcherCatalogue";
import {
  FETCHER_SETTINGS_DETAILS_QUERY_ID,
  getFirstWebsiteSharedUid,
} from "../helpers/fetcherSettingsQueryIds";
import { FETCHER_BY_BRAND_TAB } from "../helpers/urlParams";

interface Props {
  open: boolean;
  configId: string;
  onClose: () => void;
}

const PLAIN_BUTTON_SX = {
  color: "#2A2A30",
  borderColor: "#E7E7EC",
  textTransform: "none" as const,
  fontWeight: 500,
  "&:hover": { borderColor: "#E7E7EC", backgroundColor: "#F7F5FA" },
};

const ConfigBrandsModal = ({ open, configId, onClose }: Props) => {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const userData = useSelector(selectUserData);
  const websiteSharedUid = getFirstWebsiteSharedUid(userData);

  const {
    fetcherSettingsMainTablesData,
    fetcherSettingsDetailsData,
    isDetailsLoading,
  } = useSelector((state: AppState) => state.fetcherSettings);

  const config = useMemo(
    () =>
      selectCatalogueRows(fetcherSettingsMainTablesData, "config").find(
        (row) => rowId(row.item_id) === configId,
      ),
    [fetcherSettingsMainTablesData, configId],
  );

  useEffect(() => {
    if (!open || !configId || !oauthTokenHeader || !websiteSharedUid) return;
    const loadDetails = async () => {
      try {
        dispatch(setIsDetailsLoading(true));
        dispatch(setFetcherSettingsDetailsData(null));
        await FetcherSettingsEndPointLogic.getFetcherSettingsDetailsData({
          oauthTokenHeader,
          queryId: FETCHER_SETTINGS_DETAILS_QUERY_ID,
          websiteSharedUids: [websiteSharedUid],
          item_id: configId,
          level: "config",
        });
      } catch (e: any) {
        console.error("Error loading config brands:", e);
      } finally {
        dispatch(setIsDetailsLoading(false));
      }
    };
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, configId]);

  const brands = useMemo(
    () => selectDetailBrands(fetcherSettingsDetailsData),
    [fetcherSettingsDetailsData],
  );

  /**
   * Hands the brand to the By Brand tab. The whole param set is replaced so
   * this tab's own search and filters do not follow the user across.
   */
  const handleTrace = (brandId: string) => {
    onClose();
    setSearchParams(
      () => new URLSearchParams({ tab: FETCHER_BY_BRAND_TAB, brand: brandId }),
      { replace: true },
    );
  };

  const usedByBrands = config?.used_by_brands ?? 0;
  const subtitle = [
    config ? `${config.item_name} (${rowId(config.item_id)})` : null,
    config?.interface_name,
    config?.report_name,
    `${usedByBrands} brand${usedByBrands === 1 ? "" : "s"}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "660px", maxWidth: "94vw" } }}
    >
      <DialogTitle sx={{ pb: 0.5, fontSize: "20px" }}>
        Brands collecting with this config
      </DialogTitle>
      <Typography variant="body2" sx={{ px: 3, color: "#6E6E78" }}>
        {subtitle}
      </Typography>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {isDetailsLoading ? (
          <Stack spacing={1}>
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} variant="rounded" height={38} />
            ))}
          </Stack>
        ) : brands.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "#00000099", py: 4, textAlign: "center" }}
          >
            No brands are connected.
          </Typography>
        ) : (
          <Box
            sx={{
              maxHeight: 340,
              overflowY: "auto",
              border: "1px solid #E7E7EC",
              borderRadius: "9px",
            }}
          >
            {brands.map((brand, index) => (
              <Stack
                key={rowId(brand.brand_id)}
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                  px: 1.625,
                  py: 1.125,
                  fontSize: "13px",
                  borderBottom:
                    index === brands.length - 1 ? "none" : "1px solid #F0F0F3",
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    flex: "none",
                    // the brand's own active flag, not the connection's
                    backgroundColor:
                      Number(brand.brand_active) === 1 ? "#1E8E4E" : "#D6D6DC",
                  }}
                />
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: "13px" }}>
                  {brand.brand_name}{" "}
                  <Typography
                    component="span"
                    sx={{
                      color: "#9A9AA4",
                      fontWeight: 400,
                      fontSize: "0.88em",
                    }}
                  >
                    ({rowId(brand.brand_id)})
                  </Typography>
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "11px",
                    color: "#9A9AA4",
                  }}
                >
                  {brand.raw_table || "N/A"}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<NorthEastIcon sx={{ fontSize: "12px" }} />}
                  onClick={() => handleTrace(rowId(brand.brand_id))}
                  sx={{ ...PLAIN_BUTTON_SX, flex: "none" }}
                >
                  trace
                </Button>
              </Stack>
            ))}
          </Box>
        )}

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mt: 1.25,
            px: 1.5,
            py: 1.25,
            border: "1px dashed #E7E7EC",
            borderRadius: "8px",
            fontSize: "12.5px",
            color: "#6E6E78",
          }}
        >
          <NorthEastIcon sx={{ fontSize: "14px", flex: "none" }} />
          <Typography sx={{ fontSize: "12.5px", color: "#6E6E78" }}>
            Connect or disconnect a brand on{" "}
            <Link
              href="/operations/brands"
              color="secondary"
              underline="always"
            >
              Operations &#9656; Brands &#9656; Manual Configuration
            </Link>
            . This screen never changes brand connections.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" sx={PLAIN_BUTTON_SX} onClick={onClose}>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigBrandsModal;
